import axios, { AxiosError, AxiosRequestConfig } from "axios";

import { useAuth } from "../stores/auth";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8001";

export const api = axios.create({
  baseURL: `${baseURL}/api/v1`,
  timeout: 15000,
});

// Public auth endpoints: never send Authorization on these. A stale or
// otherwise-invalid token in the store would otherwise be sent and rejected
// by JWTAuthentication BEFORE the view's AllowAny check, surfacing as a
// confusing 401 on login/signup.
const PUBLIC_AUTH_ENDPOINTS = [
  "/auth/login",
  "/auth/signup",
  "/auth/refresh",
  "/auth/mfa/challenge",
  "/auth/password-reset/request",
  "/auth/password-reset/confirm",
];

function isPublicAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split("?")[0];
  return PUBLIC_AUTH_ENDPOINTS.some((p) => path === p);
}

api.interceptors.request.use((config) => {
  if (isPublicAuthEndpoint(config.url)) {
    if (config.headers) delete (config.headers as Record<string, unknown>).Authorization;
    return config;
  }
  const token = useAuth.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

type RetriableConfig = AxiosRequestConfig & { _retry?: boolean };

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = useAuth.getState().refreshToken;
  if (!refreshToken) return null;
  try {
    const response = await axios.post(`${baseURL}/api/v1/auth/refresh`, {
      refresh: refreshToken,
    });
    const newAccess = response.data.access as string;
    const newRefresh = (response.data.refresh as string | undefined) ?? refreshToken;
    useAuth.getState().setTokens({
      accessToken: newAccess,
      refreshToken: newRefresh,
      mfaRequired: useAuth.getState().mfaRequired,
    });
    return newAccess;
  } catch {
    useAuth.getState().clear();
    return null;
  }
}

// All refresh callers MUST go through this, never refreshAccessToken()
// directly. With ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION on the
// backend, two concurrent refreshes with the same refresh token cause the
// second to use a now-blacklisted token -> 401 -> clear() -> spurious
// mid-session logout. Serializing through one in-flight promise guarantees
// only a single rotation happens at a time.
function dedupedRefresh(): Promise<string | null> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// On page load: access token is not in localStorage (memory-only), so
// proactively exchange the persisted refresh token for a fresh access token.
// This avoids a 401 round-trip on the first authenticated request.
(async () => {
  await Promise.resolve(); // let Zustand persist middleware hydrate from localStorage
  const { accessToken, refreshToken } = useAuth.getState();
  if (!accessToken && refreshToken) {
    await dedupedRefresh();
  }
})();

// Proactively refresh every 270 s so the 5-minute access token never expires
// mid-session. 270 s < 300 s TTL, so there's always a 30 s safety margin.
setInterval(async () => {
  if (useAuth.getState().refreshToken) {
    await dedupedRefresh();
  }
}, 270_000);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // Trial-expired: backend returns 402 from any write endpoint when
    // tenant.status == trial and trial_ends_at has passed. Flip the
    // store flag so AppShell can render the banner. The individual
    // action still receives the error so its UI can show the inline
    // message too.
    if (status === 402) {
      useAuth.getState().setTrialExpired(true);
      return Promise.reject(error);
    }

    const skipRetry =
      !original ||
      isPublicAuthEndpoint(original.url) ||
      original._retry;

    if (status !== 401 || skipRetry) {
      return Promise.reject(error);
    }

    original._retry = true;
    const newAccess = await dedupedRefresh();
    if (!newAccess) {
      // dedupedRefresh -> refreshAccessToken already called clear() on failure,
      // so the next render PrivateRoute sees accessToken=null and navigates
      // (via react-router) to /login with state.from set to the current path.
      // Do NOT window.location.assign — a full reload tears down the SPA and
      // loses location.state.from, so the user lands on /dashboard instead of
      // the page they were on.
      return Promise.reject(error);
    }
    if (original.headers) {
      (original.headers as Record<string, string>).Authorization = `Bearer ${newAccess}`;
    } else {
      original.headers = { Authorization: `Bearer ${newAccess}` };
    }
    return api.request(original);
  },
);
